import type { Article, ArticleWithConfidence, Confidence, Category, ISODateString, ArticleSource } from "@/types/article";
import type { Database } from "@/integrations/supabase/types";

type ArticleRow = Database["public"]["Tables"]["articles"]["Row"];

const FALLBACK_IMG = "https://korthet.se/og-image.png";

/** Collapses whitespace and trims; returns "" for non-strings */
function safeText(v: unknown): string {
  if (typeof v !== "string") return "";
  let text = v.replace(/\s+/g, " ").trim();
  
  // Remove "Annons" pattern that appears at start of some articles
  text = text.replace(/^(?:Annons\s+){1,5}/i, "");
  
  return text;
}

/** 
 * Basic text cleanup - boilerplate removal now handled by database cleaning
 * This just handles basic whitespace normalization
 */
function stripBoilerplateLines(v: unknown): string {
  if (typeof v !== "string") return "";
  // Just normalize whitespace - boilerplate patterns are cleaned in DB
  return v.replace(/\s+/g, " ").trim();
}

/** Coerces a date-like value to ISO; returns undefined if invalid */
function toISODateOrUndefined(value: unknown): ISODateString | undefined {
  if (typeof value === "string" || value instanceof Date) {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d.toISOString() as ISODateString;
  }
  return undefined;
}

const CATEGORY_SET = new Set<Category>([
  "all","nyheter","sverige","varlden","politik","ekonomi","kultur","sport","noje","debatt","ledare","trendar"
]);

// Legacy categories that should be mapped to "nyheter" (the fallback category)
const BLOCKED_CATEGORIES = new Set<string>([]);

/** Narrows unknown -> Category; defaults to "nyheter", maps blocked categories to "nyheter" */
function toCategory(v: unknown): Category {
  const s = typeof v === "string" ? v : "";
  // Map blocked categories to "nyheter" (fallback)
  if (BLOCKED_CATEGORIES.has(s)) return "nyheter";
  // "all" is a filter value, not a valid article category - fallback to "nyheter"
  if (s === "all") return "nyheter";
  return CATEGORY_SET.has(s as Category) ? (s as Category) : "nyheter";
}

const CONFIDENCE_SET = new Set<Confidence>(["low", "medium", "high"]);

/** Narrows unknown -> Confidence; returns undefined for invalid values */
function toConfidence(v: unknown): Confidence | undefined {
  const s = safeText(v).toLowerCase();
  return CONFIDENCE_SET.has(s as Confidence) ? (s as Confidence) : undefined;
}

const EXT_IMAGE = /\.(?:png|jpe?g|webp|gif|avif)(?:\?.*)?$/i;

// Trusted image CDN domains that don't require file extensions
const TRUSTED_IMAGE_DOMAINS = [
  'svtstatic.se',
  'images.cdn.yle.fi',
  'images.ctfassets.net',
  'bilder.riksdagen.se',
  'images.unsplash.com',
  'gfx.aftonbladet-cdn.se',
  'static.cdn-expressen.se',
  'imgs.aftonbladet-cdn.se',
  'imageproxy.a2d.tv',
  'assets.tv4.se',
];

/**
 * Checks if URL has image format hints in query params or path
 */
function hasImageFormatHint(u: URL): boolean {
  const p = u.searchParams;
  const fmts = ["jpg","jpeg","png","webp","gif","avif"];
  if (fmts.includes((p.get("format")||"").toLowerCase())) return true;
  if (fmts.includes((p.get("fm")||"").toLowerCase())) return true;
  if (fmts.includes((p.get("f")||"").toLowerCase())) return true;
  if (fmts.includes((p.get("output")||"").toLowerCase())) return true;
  const auto = (p.get("auto")||"").toLowerCase();
  if (auto.includes("format") || fmts.includes(auto)) return true;
  const path = u.pathname.toLowerCase();
  if (/(^|\/)(image|images)(\/|$)/.test(path) || /\/upload\//.test(path)) return true;
  return false;
}

/**
 * Returns true if the URL is empty, placeholder-ish, or unusable for display.
 */
function isBadImageUrl(raw: unknown): boolean {
  if (typeof raw !== "string") return true;
  const url = raw.trim();
  if (!url) return true;

  const lc = url.toLowerCase();
  if (lc.startsWith("data:") || lc.startsWith("blob:") || lc.startsWith("about:")) return true;
  if (lc === "#" || lc === "/#" || lc.startsWith("javascript:")) return true;

  if (/(^|[\/_-])1x1(\.|$)/i.test(lc)) return true;
  if (/\.(svg)(\?|$)/i.test(lc)) return true;

  // drop obvious placeholders and logos
  if (/(placeholder|no[-]?image|default|missing|fallback|transparent|dummy|spacer|pixel|default-og-image)/i.test(lc)) return true;
  
  // drop known publisher logos that shouldn't be used as article images
  if (/\/logotypes\//i.test(lc)) return true;
  if (/logo-aftonbladet/i.test(lc)) return true;

  // Check if it's from a trusted CDN domain (these don't need file extensions)
  try {
    const urlObj = new URL(url);
    const isTrustedDomain = TRUSTED_IMAGE_DOMAINS.some(domain => 
      urlObj.hostname === domain || urlObj.hostname.endsWith('.' + domain)
    );
    
    if (isTrustedDomain) {
      // Trusted domain, no extension required - just check size
      if (url.length > 2048) return true;
      return false;
    }
    
    // Extension-less but with clear format hint? Accept it.
    if (!EXT_IMAGE.test(lc) && hasImageFormatHint(urlObj)) {
      if (url.length > 2048) return true;
      return false;
    }
  } catch {
    // Invalid URL, will be caught by normalizeImageUrl
  }

  // For non-trusted domains, require a typical raster extension
  if (!EXT_IMAGE.test(lc)) return true;

  // guard against pathological query size (>2KB)
  if (url.length > 2048) return true;

  return false;
}

/**
 * Normalizes a candidate image URL into a displayable absolute https URL if possible.
 * - handles protocol-relative //example.com/...
 * - trims, and rejects obvious non-URLs
 * - decodes HTML entities (e.g., &amp; -> &)
 */
function normalizeImageUrl(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  let u = raw.trim();
  if (!u) return null;
  
  // Decode HTML entities (e.g., &amp; -> &, &lt; -> <)
  const textarea = document.createElement('textarea');
  textarea.innerHTML = u;
  u = textarea.value;
  
  if (u.startsWith("//")) u = "https:" + u;
  try {
    const parsed = new URL(u);
    if (!["https:", "http:"].includes(parsed.protocol)) return null;
    if (parsed.protocol === "http:") parsed.protocol = "https:";
    const out = parsed.toString();
    return isBadImageUrl(out) ? null : out;
  } catch {
    return null;
  }
}

/**
 * Normalizes article source link URLs (similar to normalizeImageUrl but without image-specific filters)
 */
function normalizeLinkUrl(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  let u = raw.trim();
  if (!u) return null;
  if (u.startsWith("//")) u = "https:" + u;
  try {
    const parsed = new URL(u);
    if (!["https:", "http:"].includes(parsed.protocol)) return null;
    parsed.protocol = "https:";
    return parsed.toString();
  } catch {
    return null;
  }
}

/**
 * Normalizes and deduplicates article sources.
 * Ensures each has a valid name + url, removes empty ones.
 */
function normalizeSources(
  raw: unknown,
  fallbackSource?: string,
  fallbackUrl?: string,
  fallbackCategory?: Category
): ArticleSource[] {
  const arr = Array.isArray(raw) ? raw : [];
  const result: ArticleSource[] = [];
  const seen = new Set<string>(); // key = url||name

  const pushUnique = (name: string, url: string | null, category: Category) => {
    // Filter out "Google Trends" as it's not a real news source
    if (name.toLowerCase().includes('google trends')) return;
    
    const key = (url ?? "") + "|" + name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    result.push({ name, url, category });
  };

  for (const s of arr) {
    if (!s) continue;
    let name = safeText((s as any).name);
    let url = safeText((s as any).url);
    const rawCat = (s as any).category ?? fallbackCategory ?? "nyheter";
    const cat = toCategory(rawCat);

    const normUrl = url ? normalizeLinkUrl(url) : null;
    if (!name && fallbackSource) name = safeText(fallbackSource);

    if (!name && !normUrl) continue;
    pushUnique(name, normUrl, cat);
  }

  // Fallback single source
  if (result.length === 0 && (fallbackSource || fallbackUrl)) {
    pushUnique(
      safeText(fallbackSource) || "Okänd källa",
      fallbackUrl ? normalizeLinkUrl(fallbackUrl) : null,
      fallbackCategory || "nyheter"
    );
  }

  // Optional: cap to 5 to keep payloads small
  return result.slice(0, 5);
}

/**
 * Maps database row (snake_case) to frontend Article DTO (camelCase)
 */
export function mapDbRowToArticle(row: ArticleRow): Article {
  const title = safeText(row.title);
  const excerpt = stripBoilerplateLines(row.excerpt);
  const content = stripBoilerplateLines(row.content);

  const imageUrl = normalizeImageUrl(row.image_url) ?? FALLBACK_IMG;

  const category = toCategory(row.category);
  const publishedAt = toISODateOrUndefined(row.published_at)!; // DB enforces NOT NULL
  const updatedAt = toISODateOrUndefined(row.updated_at);
  const source = safeText(row.source);
  const trendUrl = normalizeLinkUrl(row.trend_url) || null;
  const slug = safeText(row.slug) || null;

  const sources = normalizeSources(row.sources, source, trendUrl ?? undefined, category);
  
  // Map political lean
  const politicalLeanRaw = row.ai_political_lean;
  const politicalLean = 
    politicalLeanRaw === 'left' || politicalLeanRaw === 'right' || politicalLeanRaw === 'neutral'
      ? politicalLeanRaw
      : undefined;

  return {
    id: String(row.id),
    title,
    excerpt,
    content,
    imageUrl,
    category,
    publishedAt,
    updatedAt,
    source,
    trendUrl,
    slug,
    sources,
    politicalLean,
    mergedInto: (row as any).merged_into ?? null,
    photoCredit: (row as any).photo_credit ?? null,
    author: (row as any).author ?? null,
    // Map AI-generated content if available
    aiSummary: (row as any).ai_summary ?? null,
    aiBullets: (row as any).ai_bullets ?? null,
    aiCounterPerspective: (row as any).ai_counter_perspective ?? null,
    aiLeanReasoning: (row as any).ai_lean_reasoning ?? null,
    aiSummaryQc: (row as any).ai_summary_qc ?? null,
    aiPoliticalCheck: (row as any).ai_political_check ?? null,
    aiCivicScopeScore: (row as any).ai_civic_scope_score ?? null,
    aiPoliticalScope: (row as any).ai_political_scope ?? null,
    aiScopeReason: (row as any).ai_scope_reason ?? null,
    aiPoliticalGatePassed: (row as any).ai_political_gate_passed ?? null,
    aiGateModel: (row as any).ai_gate_model ?? null,
    aiInputSource: (row as any).ai_input_source ?? null,
    aiInputLen: (row as any).ai_input_len ?? null,
    aiSummaryVersion: (row as any).ai_summary_version ?? null,
    aiAnalysisModel: (row as any).ai_analysis_model ?? null,
    // Artikelprofil v3.1
    huvudamne: (row as any).huvudamne ?? null,
    berordaAmnen: Array.isArray((row as any).berorda_amnen)
      ? ((row as any).berorda_amnen as string[]).filter((s) => typeof s === "string" && s.length > 0)
      : null,
    perspektiv: Array.isArray((row as any).perspektiv)
      ? ((row as any).perspektiv as string[]).filter((s) => typeof s === "string" && s.length > 0)
      : null,
    faktakaraktar: ((): Article["faktakaraktar"] => {
      const v = (row as any).faktakaraktar;
      return v === "nyhet" || v === "analys" || v === "opinion" || v === "referat" ? v : null;
    })(),
  };
}

/**
 * Maps database row to ArticleWithConfidence (includes confidence data)
 */
export function mapDbRowToArticleWithConfidence(row: ArticleRow): ArticleWithConfidence {
  const base = mapDbRowToArticle(row);
  const confidence = toConfidence(row.confidence_level);

  const signalsRaw = row.category_signals;
  const categorySignals = Array.isArray(signalsRaw)
    ? signalsRaw.map(safeText).filter(Boolean)
    : undefined;

  return {
    ...base,
    confidence,
    categorySignals,
  };
}

/**
 * Maps an array of database rows to frontend Article DTOs
 */
export function mapDbRowsToArticles(rows: ArticleRow[] | null | undefined): Article[] {
  return (rows ?? []).map(mapDbRowToArticle);
}

/**
 * Maps an array of database rows to ArticleWithConfidence DTOs
 */
export function mapDbRowsToArticlesWithConfidence(rows: ArticleRow[] | null | undefined): ArticleWithConfidence[] {
  return (rows ?? []).map(mapDbRowToArticleWithConfidence);
}
